/* eslint-disable no-template-curly-in-string */
/**
 * @module msysWorkflow
 * @description
 * Core workflow functionality.
 * msysWorkflow.js implements msysWorkflow class that provides basic workflow
 * functionality used in both client and server:
 * - transformation of expressions to JavaScript functions
 * - transformation of expressions to SQL queries
 * - transformation of workflow definition shortcuts to proper full definitions
 * - calculation of actions allowed for given object and context (application and user)
 *
 * Example of use:
 * const mwf = require('msysWorkflow');
 * var cond = [
 *   "or",
 *   ["==","${assigner}","${actor.user_id}"],
 *   ["==","${assignee}","${actor.user_id}"],
 * ]
 * cond = wf.normalizeExpression(cond);
 * cond = wf.fillExpression({
 *   data: { assigner: 11, assignee: 12 }
 * }, cond);
 * var fcn = wf.expressionToFunction({}, cond);
 * console.log("RESULT: ", await fcn({
 *   actor: { user_id: 15 }
 * }));
 */

const mcrypt = require('msysCrypt')
const mcron = require('msysCron')
const mmath = require('msysMath')

const WORKFLOW_MANDATORY_FIELDS = {
  id: { description: 'unique ID', type: 'id' },
  state: { description: 'workflow state', type: 'string', prefix: '$wf.state.' },
  created_dt: { description: 'date time of creation', type: 'datetimeus' },
  updated_dt: { description: 'date time of last update', type: 'datetimeus' },
  timer_dt: { description: 'date time of nearest expired timer', type: 'datetimeus' },
  roles: { description: 'comma separate string with zone:access roles', type: 'string' },
}
const OPERATOR_ALIASES = { '!': 'NOT', '<>': '!=' }
const OPERANDS_COUNT = {
  TRUE: 0,
  FALSE: 0,
  NOW: 0,
  UNOW: 0,
  NOT: 1,
  GET: '1+',
  AND: '1+',
  OR: '1+',
  MATCH: 2,
  IMATCH: 2,
  '==': 2,
  '!=': 2,
  '<': 2,
  '<=': 2,
  '>': 2,
  '>=': 2,
  '+': 2,
  '-': 2,
  '*': 2,
  '/': 2,
  '++': 1,
  '--': 1,
  FILL: 2,
  SET: '2+',
  '?': '2+',
  INTEGER: 1,
  DECIMAL: 1,
  STRING: 1,
}
const LIST_ITEM_SEPARATOR = '∫'

const ATTRIBUTES_TO_REPLACE_WF_WILDCARD = ['header', 'header2', 'headericon', 'label', 'label2', 'prefix', 'before', 'after', 'value', 'color', 'itemlabel', 'itemlabel2', 'itemvalue', 'itemcolor', 'itemicon']
const FIELD_COPIED_ATTRIBUTES = ['type', 'input', 'class', 'style', 'rows', 'format', 'transform', 'readonly', 'disabled', 'multi', 'auto', 'prefix', 'label', 'label2', 'value', 'before', 'after', 'blank', 'hidden', 'itemlabel', 'itemlabel2', 'itemvalue', 'itemcolor', 'itemicon', 'split', 'iconleft', 'iconright', 'linkmod', 'linkid', 'mime', 'compute', 'recompute', 'syntax', 'min', 'max']

const ITEM_TEMPLATES = {
  INVISIBLE: { tag: 'mele-input', type: 'invisible' },
  HISTORY: {
    tag: 'mele-group',
    key: 'history',
    label: '$wf.history',
    items: [
      {
        tag: 'mele-table',
        key: 'logs',
        class: 'inline readonly',
        limit: 100,
        sort: ['data.log_dt-'],
        rowattributes: ['log_action'],
        columns: ['log_dt', 'log_action', 'log_userid', 'log_details'],
      },
    ],
  },
  WORKFLOW: { tag: 'div', key: 'workflow', label: '$wf.workflow' },
}

const FIELD_DEFAULT_INPUT = {
  id: { key: 'id', label: '$wf.id', tag: 'mele-input', type: 'string', readonly: true },
  created_dt: { key: 'created_dt', label: '$wf.created_dt', tag: 'mele-input', type: 'datetimeus', readonly: true },
  updated_dt: { key: 'updated_dt', label: '$wf.updated_dt', tag: 'mele-input', type: 'datetimeus', readonly: true },
  timer_dt: { key: 'timer_dt', label: '$wf.timer_dt', tag: 'mele-input', type: 'datetimeus', readonly: true },
  state: { key: 'state', label: '$wf.state', tag: 'mele-input', type: 'sdropdown', auto: true, prefix: '$wf.state.', itemlabel: '${prefix}${key}', readonly: true },
  log_dt: { key: 'log_dt', label: '$wf.log_dt', tag: 'mele-input', type: 'datetimeus', readonly: true },
  log_action: { key: 'log_action', label: '$wf.action', tag: 'mele-input', type: 'string', prefix: '$wf.action.', itemlabel: '${prefix}${key}', readonly: true },
  log_userid: { key: 'log_userid', label: '$wf.actor', tag: 'mele-input', type: 'string', prefix: 'user.fullname.', itemlabel: '${prefix}${key}', readonly: true },
}

export default class msysWorkflow {
  constructor(context_object, def) {
    if(!_.isObjectLike(def)) return
    def.id = _.toString(def.id)
    if(def.id==='') return

    this.id = def.id
    this.version = def.version || Date.now()
    this.icon = def.icon
    this.id_generator = _.toString(def.id_generator)
    this.sqlview = _.toString(def.sqlview)
    this.sqlview_log = _.toString(def.sqlview_log)
    this.notifications = _.toString(def.notifications)
    this.logs = def.logs!==false
    this.allow = def.allow
    this.fields = {}
    this.sql_queries = def.sql_queries || {}
    this.forms = {}
    this.acls = []
    this.states = {}
    this.context_object = context_object

    this.addFields(WORKFLOW_MANDATORY_FIELDS) // here to ensure these will be first fields
    this.addFields(def.fields)
    this.addFields(WORKFLOW_MANDATORY_FIELDS) // added second time to ensure defintion did not overwrote us
    this.addForms(def.forms)
    this.addTranslations(def.translations)
    this.addScripts(def.scripts)
    this.addStyles(def.styles)
    this.addAcls(def.acls)
    this.addStates(def.states)
    this.addStates({ WIPED: {} }) // ensure there is DELETED state
  }

  async getModel(actor) {
    if(actor && this.allow) {
      const CTX = {
        dt: unow(),
        actor,
        data: {},
        // module-level gate: there is no concrete record here, so ACCESS must test role
        // presence (any zone), not zone-match an empty placeholder object - see hasAccess
        no_object: true,
      }
      const condition = this.conditionToExpression(this.allow)
      const test = this.expressionToFunction(CTX, condition)
      if(!(await test.call(this.context_object, CTX))) return // actor not allowed to access the model
    }
    let model = _.pick(this, ['id', 'version', 'icon', 'id_generator', 'sqlview', 'sqlview_log', 'notifications', 'logs', 'fields', 'sql_queries', 'forms', 'acls', 'states', 'translations', 'scripts', 'styles'])
    model = JSON.parse(JSON.stringify(model))
    return model
  }

  isCalculated() {
    if(this.sqlview) return true
    for(const field in this.fields) {
      if(this.fields[field].sql || this.fields[field].compute) return true
    }
    return false
  }

  addFields(fields) {
    if(!_.isObjectLike(fields)) return
    for(const f in fields) {
      if(!f) continue
      const field = fields[f]
      if(_.isObjectLike(field)) {
        if(typeof field.type!=='string' || field.type.length===0) {
          this.context_object.warn('Invalid workflow field: '+JSON.stringify(field))
          continue
        }
        this.fields[f] = _.omit(field, ['description'])
      }else{
        delete this.fields[f]
      }
    }
  } // addFields

  addForms(forms) {
    if(!_.isObjectLike(forms)) return
    for(const f in forms) {
      if(!f) continue
      const form = forms[f]
      if(_.isObjectLike(form)) {
        form.key = f
        this.forms[f] = form
      }else{
        delete this.forms[f]
      }
    }
  } // addForms

  addTranslations(translations) {
    if(typeof (translations)!=='string') translations=null
    if(_.isNil(translations)) {
      delete this.translations
    }else{
      this.translations = translations
      LANG.createOptions('wf.'+this.id+'.', [], true) // delete all existing translations
      translations = translations.replace(/\n(\w)/g, (m, m1)=>'\nwf.'+this.id+'.'+m1)
      LANG.loadTranslation(translations, ()=>{})
      LANG.registerFallback('wf.'+this.id+'.', 'wf.generic.')
    }
  } // addTranslations

  addScripts(scripts) {
    this.scripts = scripts
    this.parsed_scripts = {}
    // eslint-disable-next-line no-unused-vars
    const WORKFLOW = this.parsed_scripts
    const LIB = {}
    LIB.schema_types = this.context_object._schema_types
    LIB.schema_actions = this.context_object._schema_actions

    try { LIB.os = require('os') }catch(e) {}
    try { LIB.fs = require('fs') }catch(e) {}
    try { LIB.path = require('path') }catch(e) {}
    try { LIB.net = require('net') }catch(e) {}
    try { LIB.child_process = require('child_process') }catch(e) {}
    try { LIB.crypto = require('crypto') }catch(e) {}
    try { LIB.zlib = require('zlib') }catch(e) {}
    try { LIB.http = require('http') }catch(e) {}
    try { LIB.https = require('https') }catch(e) {}
    try { LIB.msysDB = require('msysDB') }catch(e) {}
    try { LIB.msysFS = require('msysFS') }catch(e) {}
    try { LIB.msysCrypt = require('msysCrypt') }catch(e) {}
    try { LIB.msysGeo = require('msysGeo') }catch(e) {}
    try { LIB.msysAV = require('../msysAV') }catch(e) {}
    try { LIB.avAlgo = require('../msysAV/src_em/algo.js') }catch(e) {}
    try { LIB.avMatrix = require('../msysAV/src_em/matrix.js') }catch(e) {}
    try { LIB.avDraw = require('../msysAV/src_em/draw.js') }catch(e) {}
    try { LIB.avColor = require('../msysAV/src_em/color.js') }catch(e) {}
    try { LIB.avH264 = require('../msysAV/src_em/H264.js') }catch(e) {}
    try { LIB.avMP4 = require('../msysAV/src_em/MP4.js') }catch(e) {}
    try { LIB.avBMFF = require('../msysAV/src_em/BMFF.js') }catch(e) {}
    try { LIB.avNALs = require('../msysAV/src_em/NALs.js') }catch(e) {}
    try { LIB.avSPS = require('../msysAV/src_em/SPS.js') }catch(e) {}
    try {
      LIB.msysMessage = {
        ...require('../msysMessage/base.js'),
        ...require('../msysMessage/binary.js'),
        ...require('../msysMessage/equal.js'),
        ...require('../msysMessage/filter.js'),
      }
    }catch(e) {}
    try {
      Object.assign(LIB.msysMessage, {
        ...require('msysMessage/crypto.js'),
        ...require('msysMessage/file.js'),
      })
    }catch(e) {}
    try { LIB.msysListener = require('msysListener') }catch(e) {}
    try { LIB.qrcode = require('qrcode') }catch(e) {}
    try { LIB.ftp = require('basic-ftp') }catch(e) {}
    try { LIB.msysXLSX = require('msysXLSX') }catch(e) {}
    try{
      // eslint-disable-next-line no-eval
      eval(this.scripts)
    }catch(e) {
      this.context_object.error('Error in workflow scripts: '+e)
    }
  } // addScripts

  addStyles(styles) {
    this.styles = styles
  } // addStyles

  addAcls(acls) {
    if(!Array.isArray(acls)) return
    acls.forEach(acl=>{
      if(!_.isObjectLike(acl)) return

      // IMPORTANT! ACL evaluates allowed fields using definition of fields at the time of adding ACL
      // therefore ACL must be added AFTER the fields
      const condition = this.conditionToExpression(acl.condition)
      const form = _.toString(acl.form)
      let fields = {}
      const acl_fields = Array.isArray(acl.fields) ? acl.fields : []
      acl_fields.forEach(field_re=>{
        field_re = _.toString(field_re)
        if(field_re === '.*' || field_re === '.+') field_re = '[^_].*'
        let remove = false
        if(field_re[0] === '!') {
          remove = true
          field_re = field_re.substr(1)
        }
        field_re = '^' + field_re + '$'
        try{
          const re = new RegExp(field_re)
          for(const f in this.fields) {
            if(re.test(f)) {
              if(remove) {
                delete fields[f]
              }else {
                fields[f] = 1
              }
            }
          }
        }catch(e) {}
      })
      fields = Object.keys(fields)

      if(form || fields.length>0) { this.acls.push({ condition, form, fields }) }
    })
  } // addAcls

  addStates(states) {
    if(!_.isObjectLike(states)) return
    for(const s in states) {
      let state = states[s]
      if(!_.isObjectLike(state)) continue
      state = {
        draw: _.isObjectLike(state.draw) ? state.draw : {},
        entry_steps: this.stepsToExpression(state.entry_steps),
        entry_steps_db: this.stepsToExpression(state.entry_steps_db),
        exit_steps: this.stepsToExpression(state.exit_steps),
        exit_steps_db: this.stepsToExpression(state.exit_steps_db),
        actions: _.isObjectLike(state.actions) ? state.actions : {},
      }

      for(const a in state.actions) {
        const action = state.actions[a]
        if(!_.isObjectLike(action)) {
          delete state.actions[a]
          continue
        }
        state.actions[a] = {
          condition: this.conditionToExpression(action.condition),
          color: _.toString(action.color),
          icon: _.toString(action.icon),
          iconleft: _.toString(action.iconleft),
          iconright: _.toString(action.iconright),
          form: _.toString(action.form),
          client_script: action.client_script,
          post_steps: this.stepsToExpression(action.post_steps),
          post_steps_db: this.stepsToExpression(action.post_steps_db),
          target_state: _.toString(action.target_state),
          log: this.logs && action.log!==false,
          log_fields: Array.isArray(action.log_fields) ? action.log_fields : [],
          confirm: action.confirm,
          group: action.group,
          popup: action.popup,
        }
        const ttl = parseInt(('ttl' in action) ? action.ttl : this.context_object?.config?.ttl)
        if(!isNaN(ttl) && ttl>0) {
          state.actions[a].ttl = ttl
        }
      }

      this.states[s] = state
    }
  } // addStates

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * transforsm provided workflow condition into normalized and simplified workflow expression
 * @param {array} cond - condition expression
 */
  conditionToExpression(cond) {
    cond = this.normalizeExpression(cond) // unify operators
    cond = this.fillExpression({}, cond) // evaluate constant expressions
    return cond
  } // conditionToExpression

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * transforsm provided list of steps into normalized and simplified workflow expression
 * @param {array} steps - array of expressions
 */
  stepsToExpression(steps) {
    steps = Array.isArray(steps) ? ['DO'].concat(steps) : []
    steps = this.normalizeExpression(steps) // unify operators
    steps = this.fillExpression({}, steps) // evaluate constant expressions
    if(!Array.isArray(steps)) return []
    return steps
  } // stepsToExpression

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * transforsm provided workflow expression into unified form
 * - make operators uppercase
 * - transform aliases to main operator name
 * - transforms variable interpolations ${} into get() operators
 * @param {object} expr - expression
 */
  normalizeExpression(expr) {
    if(Array.isArray(expr)) {
    // it is expression, i.e. array with operator and operands
      if(expr.length===0) return true // empty array is treated as TRUE (i.e. empty condition is satisfied)
      let operator = _.toString(expr[0]).toUpperCase()
      operator = OPERATOR_ALIASES[operator] || operator
      const operands = expr.slice(1)

      // default "data" domain for GET/SET
      if(operator==='GET' && operands.length===1) operands.unshift('data')
      if(operator==='SET' && operands.length===2) operands.unshift('data')
      if(operator==='PUT' && operands.length===2) operands.unshift('data')
      if(operator==='PULL' && operands.length===1) operands.unshift('data')

      // limit operands if needed
      const op_count = parseInt(OPERANDS_COUNT[operator])
      if(op_count>0) {
        if(operands.length<op_count) return false
        if(String(op_count) === String(OPERANDS_COUNT[operator])) operands.length=op_count // if just number is provided, limit operands to desired count; if e.g. 3+, just keep as it is
      }

      expr = operands.map(this.normalizeExpression.bind(this))
      expr.unshift(operator)
      return expr
    }else if(typeof expr==='object' && expr!=null) {
      const out = {}
      for(const p in expr) {
        const value = this.normalizeExpression(expr[p])
        if(typeof (value)!=='undefined') { out[p] = value }
      }
      return out
    }else if(typeof expr==='string') {
    // it is literal string
    // perform interpolation of known variables
      expr = expr.replace(/\$\{([^}.]+)\}/g, (m, p1)=>'${data.'+p1+'}') // transfrm shorthand ${xxx} to ${data.xxx}

      // replace literal with concat expression containing constant and expression parts
      const parts = []
      let m
      const re = /^([\s\S]*?)\$\{(\w+)\.([^}]+)\}([\s\S]*)/
      while((m = re.exec(expr))) {
        if(m[1]!=='') parts.push(m[1])
        parts.push(['GET', m[2], m[3]])
        expr = m[4]
      }
      if(expr!=='') parts.push(expr)
      if(parts.length===0) return ''
      if(parts.length===1) return parts[0]
      return ['CONCAT'].concat(parts) // return as concat operation
    }else{
    // it is other literal, keep as it is
      return expr
    }
  } // normalizeExpression

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * simplifies expression tree by filling variables with provided values
 * and transforming as much of the expression into lliteral value
 * @param {object} values - hash of known values; contains hashes of domains with particular values
 * @param {array} expr - normalized expression in form [operator, operands...]
 */
  fillExpression(values, expr) {
    if(Array.isArray(expr)) {
    // it is expression, i.e. array with operator and operands
      if(expr.length===0) return true // empty array is treated as TRUE (i.e. empty condition is satisfied)
      const operator = expr[0]
      let operands = expr.slice(1).map(this.fillExpression.bind(this, values))
      const all_literals = operands.filter(Array.isArray).length===0

      if(operator==='GET') {
      // GET on domain provided in values can be immediately evaluated and returned as value
        if(operands[0] in values) return _.get(values[operands[0]], operands[1])
      }else if(operator==='CONCAT') {
      // concatenate series of literal (non-expression) operands into one string
        const parts = []
        operands.forEach(operand=>{
          if(Array.isArray(operand)) {
          // we cannot concatenate expressional operand with anything
            parts.push(operand)
          }else{
            operand = _.toString(operand)
            if(parts.length>0 && typeof (parts[parts.length-1])==='string') {
              // we can concatenate non-expressional operand with previous non-expressional operand
              parts[parts.length-1] = parts[parts.length-1] + operand
            }else {
              parts.push(operand)
            }
          }
        })
        if(parts.length===1) return parts[0]
        operands = parts
      }else if(operator==='TRUE') {
        return true
      }else if(operator==='FALSE') {
        return false
      }else if(operator==='IS') {
        const op = operands[0]
        if(all_literals) {
          if(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0') return false
          if(/^true$/i.test(op)) return true
          return !!op
        }
        return expr
      }else if(operator==='NOT') {
        const op = operands[0]
        if(all_literals) {
          if(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0') return true
          if(/^true$/i.test(op)) return false
          return !op
        }
        return expr
      }else if(operator==='MATCH') {
        if(all_literals) {
          try{
            const re = new RegExp(operands[1], 'm')
            return re.test(operands[0])
          }catch(e) {
            return false // invalid regexp
          }
        }
      }else if(operator==='IMATCH') {
        if(all_literals) {
          try{
            const re = new RegExp(operands[1], 'im')
            return re.test(operands[0])
          }catch(e) {
            return false // invalid regexp
          }
        }else{
          return expr
        }
      }else if(operator==='==') {
        if(all_literals) return operands[0]==operands[1]
        if(_.isEqual(operands[0], operands[1])) return true // equal expressions are also always true
      }else if(operator==='!=') {
        if(all_literals) return operands[0]!=operands[1]
        if(_.isEqual(operands[0], operands[1])) return false // equal expressions are also always true
      }else if(operator==='<' && all_literals) {
        if(typeof operands[0]==='number' || typeof operands[1]==='number') {
          return parseFloat(operands[0]) < parseFloat(operands[1])
        }else{
          return operands[0] < operands[1]
        }
      }else if(operator==='<=' && all_literals) {
        if(typeof operands[0]==='number' || typeof operands[1]==='number') {
          return parseFloat(operands[0]) <= parseFloat(operands[1])
        }else{
          return operands[0] <= operands[1]
        }
      }else if(operator==='>' && all_literals) {
        if(typeof operands[0]==='number' || typeof operands[1]==='number') {
          return parseFloat(operands[0]) > parseFloat(operands[1])
        }else{
          return operands[0] > operands[1]
        }
      }else if(operator==='>=' && all_literals) {
        if(typeof operands[0]==='number' || typeof operands[1]==='number') {
          return parseFloat(operands[0]) >= parseFloat(operands[1])
        }else{
          return operands[0] >= operands[1]
        }
      }else if(operator==='+' && all_literals) {
        let sum = 0
        for(const operand of operands) {
          sum += parseFloat(operand)
        }
        if(isNaN(sum)) {
          return null
        }else {
          return sum
        }
      }else if(operator==='-' && all_literals) {
        return operands[0] - operands[1]
      }else if(operator==='*' && all_literals) {
        return operands[0] * operands[1]
      }else if(operator==='/' && all_literals) {
        return operands[0] / operands[1]
      }else if(operator==='AND') {
        const expression_operands = []
        for(let i=0; i<operands.length; i++) {
          if(Array.isArray(operands[i])) {
            expression_operands.push(operands[i])
          }else{
            const op = operands[i]
            const is_true = /^true$/i.test(op) || !(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0') || !!op
            if(!is_true) {
            // true (non-expression) value validates whole OR expression
              return false
            }
          }
        }
        if(expression_operands.length===0) return true // only true literals were found
        if(expression_operands.length===1) return expression_operands[0] // if only one expressional operand remains, return it directly
        operands = expression_operands // several remaining expressional operands returned with operator
      }else if(operator==='OR') {
        const expression_operands = []
        for(let i=0; i<operands.length; i++) {
          if(Array.isArray(operands[i])) {
            expression_operands.push(operands[i])
          }else{
            const op = operands[i]
            const is_true = /^true$/i.test(op) || !(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0') || !!op
            if(is_true) {
            // true (non-expression) value validates whole OR expression
              return true
            }
          }
        }
        if(expression_operands.length===0) return false // only false literals were found
        if(expression_operands.length===1) return expression_operands[0] // if only one expressional operand remains, return it directly
        operands = expression_operands // several remaining expressional operands returned with operator
      }else if(operator==='?') {
      // if condition is value (not expression), we can immediately decide what to return
        if(!Array.isArray(operands[0])) {
          return(operands[0] ? operands[1] : operands[2])
        }
      }else if(operator==='TIME') {
        if(values.cron===true) return true
        const dt = new Date(values.cron*1000)
        if(!isNaN(dt.valueOf())) {
          const result = mcron.isMatchingTime(operands[0], dt)
          return result
        }
      }else if(operator==='TIMEOUT') {
        const sec = values.cron
        if(sec===true) return true
        if(sec>0 && sec < 10000000000) {
          let start_sec = Math.floor(operands[0])
          if(start_sec > 10000000000) start_sec = Math.floor(start_sec/1000)
          if(start_sec > 10000000000) start_sec = Math.floor(start_sec/1000)
          const timeout = parseInt(operands[1])
          const timeout_sec = start_sec + timeout
          const result = timeout_sec <= sec
          return result
        }
      }

      // by default return operator with processed operands
      return [operator].concat(operands)
    }else if(typeof expr==='object' && expr!=null) {
      const out = {}
      for(const p in expr) {
        const value = this.fillExpression(values, expr[p])
        if(typeof (value)!=='undefined') { out[p] = value }
      }
      return out
    }else{
    // it is other literal, keep as it is
      return expr
    }
  } // fillExpression

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * converts generic expression tree into Javascript function able to evaluate the expression
 * @param {object} CTX - hash of known values
 * @param {array} expr - expression in form [operator, operands...]
 */
  expressionToFunction(CTX, expr) {
    // eslint-disable-next-line no-new-func
    if(expr===false || expr===true) return new Function('return '+expr)
    // eslint-disable-next-line no-new-func
    if(!Array.isArray(expr) || expr.length===0) return new Function('return true')
    expr = this.fillExpression(CTX, expr)
    // eslint-disable-next-line no-new-func
    if(expr===true || expr===false) return new Function('return '+expr)
    let def = this._expressionToFunctionPart(CTX, expr)
    def = `
    var WF = this.workflow
    CTX = CTX || {}
    try{ return !!(${def}) }catch(e){ console.error('error calling fcn: ',e); return false }`
    try{
      const AsyncFunction = Object.getPrototypeOf(async function() {}).constructor
      const fcn = new AsyncFunction('CTX', def)
      return fcn
    }catch(e) {
      this.context_object.error('Invalid expression to function: '+JSON.stringify(expr)+' => '+def+'   CTX:'+JSON.stringify(CTX))
      // eslint-disable-next-line no-new-func
      return new Function('CTX', 'return false')
    }
  } // expressionToFunction

  fcnGet(CTX, domain, field) {
    let obj = CTX[domain]
    if(field === undefined || field === null) return obj
    const path = field.split('.')
    while(obj && path.length>1) {
      obj = obj?.[path.shift()]
    }
    return _.isObjectLike(obj) ? obj[field] : undefined
  } // fcnGet

  fcnSet(CTX, domain, field, value) {
    if(_.isObjectLike(CTX[domain])) {
      CTX[domain][field] = value
      return value
    }
  } // fcnSet

  fcnPut(CTX, domain, path, value) {
    const obj = CTX[domain]
    if(!_.isObjectLike(obj)) return
    try{
      _.set(obj, path, value)
      return value
    }catch(e) {
      this.error('put error: '+e)
    }
  } // fcnPut

  fcnPull(CTX, domain, path) {
    const obj = CTX[domain]
    if(!_.isObjectLike(obj)) return
    try{
      _.unset(obj, path)
      return true
    }catch(e) {
      this.error('pull error: '+e)
      return false
    }
  } // fcnPull

  fcnCopy(CTX, operands) {
    if(!_.isObjectLike(CTX.data)) return
    operands = operands || []
    const tests = operands.map(a=>{ let re=null; try{ re = new RegExp('^'+a+'$') }catch(e) {} return re }).filter(a=>!!a)
    const params = CTX.params || {}
    Object.keys(params).forEach(p=>{
      for(let i=0; i<tests.length; i++) {
        if(tests[i].test(p)) {
          CTX.data[p] = params[p]
          break
        }
      }
    })
    return true
  } // fcnCopy

  fcnCopyExcept(CTX, operands) {
    if(!_.isObjectLike(CTX.data)) return
    operands = operands || []
    const tests = operands.map(a=>{ let re=null; try{ re = new RegExp('^'+a+'$') }catch(e) {} return re }).filter(a=>!!a)
    const skip = { state: true, created_dt: true, updated_dt: true, roles: true }
    const params = CTX.params || {}
    Object.keys(params).forEach(p=>{
      for(let i=0; i<tests.length; i++) { if(skip[p] || tests[i].test(p)) return }
      CTX.data[p]=params[p]
    })
    return true
  } // fcnCopyExcept

  fcnNotify(CTX, level, message) {
    if(!_.isObjectLike(CTX.data)) return
    if(!message) message = `{{wf.${this.id}.Object}} ${CTX.data.id} {{wf.${this.id}.state.${CTX.target_state}}}∫{{user.fullname.${CTX.actor.user_id}}}`
    CTX.data._notify=level+message
    return message
  } // fcnNotify

  fcnNotifyMe(CTX, level, message) {
    if(!_.isObjectLike(CTX.data)) return
    if(!message) message = `{{wf.${this.id}.Object}} ${CTX.data.id} {{wf.${this.id}.state.${CTX.target_state}}}`
    CTX.data._notifyme=level+message
    return message
  } // fcnNotifyMe

  fcnMatch(str, re, ignorecase) {
    try{
      re = new RegExp(re, ignorecase?'im':'m')
      return re.test(str)
    }catch(e) { return false }
  } // fcnMatch

  async fcnFunction(CTX, operands) {
    const fcnName = operands.shift()
    const fcn = this.workflow.parsed_scripts[fcnName] || this[fcnName]

    if(typeof (fcn)!=='function') {
      this.error('missing function: '+fcnName)
    }else{
      try {
        operands.unshift(CTX)
        return await fcn.apply(this, operands)
      }catch(e) {
        this.error('problem with function: '+fcnName+': '+e)
      }
    }
  } // fcnFunction

  async fcnScript(CTX, operands) {
    const scriptName = operands.shift()
    const fcn = this.workflow.parsed_scripts[scriptName] || this[scriptName]
    if(typeof (fcn)!=='function') {
      this.error('missing script: '+scriptName)
      return false
    }else{
      try {
        operands.unshift(CTX)
        await fcn.apply(this, operands)
        return true
      }catch(e) {
        this.error('problem with script: '+scriptName+':'+e.stack)
        return false
      }
    }
  } // fcnScript

  async fcnCoerce(CTX, operands) {
    const scriptName = operands.shift()
    let [varDomain, varName] = operands.shift().split('.')
    if(typeof (varName)==='undefined') {
      varName = varDomain
      varDomain = 'data'
    }
    const fcn = this.workflow.parsed_scripts[scriptName]
    if(typeof (fcn)!=='function') {
      this.error('missing coerce: '+scriptName)
      return false
    }else{
      const varValue = this.workflow.fcnGet.call(this, CTX, varDomain, varName)
      operands.unshift(varValue)
      try {
        const result = await fcn.apply(this, operands)
        this.workflow.fcnSet.call(this, CTX, varDomain, varName, result)
        return true
      }catch(e) {
        this.error('problem with coerce: '+scriptName)
        return false
      }
    }
  } // fcnCoerce

  fcnDebug(CTX, operands) {
    let str = JSON.stringify(CTX)
    if(operands && (!Array.isArray(operands) || operands.length>0)) str += ' WITH OPERANDS: ' + JSON.stringify(operands)
    this.debug(str)
  } // fcnDebug

  fcnWatcher(CTX) {
    return !!_.get(CTX, 'actor.watcher_id')
  } // fcnWatcher

  fcnActorAdd(CTX, field) {
    const user_id = _.get(CTX, 'actor.user_id')
    if(user_id>0) {
      return this.fcnListAdd(CTX, [field, user_id])
    }else{
      return false
    }
  } // fcnUserAdd

  fcnActorDel(CTX, field) {
    const user_id = _.get(CTX, 'actor.user_id')
    if(user_id>0) {
      return this.fcnListDel(CTX, [field, user_id])
    }else{
      return false
    }
  } // fcnActorDel

  fcnListAdd(CTX, operands) {
    const field = operands.shift()
    let value = _.get(CTX, 'data.'+field) || ''
    let values = value.split(LIST_ITEM_SEPARATOR)
    values = _.union(values, operands)
    values = values.filter(v=>v!='')
    value = values.length>0 ? values.join(LIST_ITEM_SEPARATOR) : ''
    _.set(CTX, 'data.'+field, value)
    return value
  } // fcnListAdd

  fcnListDel(CTX, operands) {
    const field = operands.shift()
    let value = _.get(CTX, 'data.'+field) || ''
    let values = value.split(LIST_ITEM_SEPARATOR)
    values = _.difference(values, operands)
    values = values.filter(v=>v!='')
    value = values.length>0 ? values.join(LIST_ITEM_SEPARATOR) : ''
    _.set(CTX, 'data.'+field, value)
    return value
  } // fcnListDel

  fcnAllInList(CTX, operands) {
    const field = operands.shift()
    if(operands.length===0) return true
    const value = _.get(CTX, 'data.'+field) || ''
    const values = value.split(LIST_ITEM_SEPARATOR)
    for(let i=0; i<operands.length; i++) {
      if(values.indexOf(operands[i])<0) return false
    }
    return true
  } // fcnAllInList

  fcnAnyInList(CTX, operands) {
    const field = operands.shift()
    if(operands.length===0) return false
    const value = _.get(CTX, 'data.'+field) || ''
    const values = value.split(LIST_ITEM_SEPARATOR)
    for(let i=0; i<operands.length; i++) {
      if(values.indexOf(operands[i])>=0) return true
    }
    return false
  } // fcnAnyInList

  fcnIsAppType(CTX, types) {
    types = _.toString(types).split(',')
    return types.indexOf(CTX.actor.apptype)>=0
  } // fcnIsAppType

  fcnFill(CTX, input, value) {
    try{
      CTX.form.forAll('@{'+input+'}', el=>{ el.value=value })
    }catch(e) {}
  } // fcnFill

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * converts generic expression tree into Javascript function definition part
 * @param {array} expr - expression in form [operator, operands...]
 */
  _expressionToFunctionPart(CTX, expr) {
    if(Array.isArray(expr)) {
    // it is expression, i.e. array with operator and operands
      if(expr.length===0) return true // empty array is treated as TRUE (i.e. empty condition is satisfied)
      const operator = expr[0]
      let operands = expr.slice(1)
      operands = operands.map(this._expressionToFunctionPart.bind(this, CTX))

      if(operator==='NOW' || operator==='UNOW') {
        return 'unow()'
      }else if(operator==='INTEGER') {
        return `parseInt(${operands[0]})`
      }else if(operator==='DECIMAL') {
        return `parseFloat(${operands[0]})`
      }else if(operator==='STRING') {
        return `String(${operands[0]??''})`
      }else if(operator==='IS') {
        return `!!${operands[0]}`
      }else if(operator==='NOT') {
        return `!${operands[0]}`
      }else if(operator==='MATCH') {
        return `WF.fcnMatch(${operands[0]}, ${operands[1]}, false)`
      }else if(operator==='IMATCH') {
        return `WF.fcnMatch(${operands[0]}, ${operands[1]}, true)`
      }else if(operator==='++') {
        return `((parseInt(${operands[0]})??0) + 1)`
      }else if(operator==='--') {
        return `((parseInt(${operands[0]})??0) - 1)`
      }else if(['==', '!=', '<', '<=', '>', '>=', '+', '-', '*', '/'].includes(operator)) {
        return `(${operands[0]} ${operator} ${operands[1]})`
      }else if(operator==='IN') {
        const first = operands.shift()
        return '(' + operands.map(op=>`${first}==${op}`).join(' || ') + ')'
      }else if(operator==='?') {
        return `(${operands[0]} ? ${operands[1]} : ${operands[2]})`
      }else if(operator==='AND') {
        return '('+operands.join(' && ')+')'
      }else if(operator==='OR') {
        return '('+operands.join(' || ')+')'
      }else if(operator==='DO') {
        if(operands.length<1) return true
        if(operands.length===1) return operands[0]
        return '('+operands.join(', ')+')'
      }else if(operator==='GET') {
        if(operands.length===1) operands.unshift('data')
        return `WF.fcnGet(CTX, ${operands[0]}, ${operands[1]})`
      }else if(operator==='SET') {
        return `WF.fcnSet(CTX, ${operands[0]}, ${operands[1]}, ${operands[2]})`
      }else if(operator==='PUT') {
        return `WF.fcnPut(CTX, ${operands[0]}, ${operands[1]}, ${operands[2]})`
      }else if(operator==='PULL') {
        return `WF.fcnPull(CTX, ${operands[0]}, ${operands[1]})`
      }else if(operator==='COPYALL') {
        return 'WF.fcnCopyExcept(CTX)'
      }else if(operator==='COPY') {
        return `WF.fcnCopy(CTX, [${operands.join(',')}])`
      }else if(operator==='COPYEXCEPT') {
        return `WF.fcnCopyExcept(CTX, [${operands.join(',')}])`
      }else if(operator==='NOTIFY') {
        return `WF.fcnNotify(CTX, 0, ${operands[0]})`
      }else if(operator==='NOTIFYME') {
        return `WF.fcnNotifyMe(CTX, 0, ${operands[0]})`
      }else if(operator==='WARN') {
        return `WF.fcnNotify(CTX, 1, ${operands[0]})`
      }else if(operator==='WARNME') {
        return `WF.fcnNotifyMe(CTX, 1, ${operands[0]})`
      }else if(operator==='ALERT') {
        return `WF.fcnNotify(CTX, 2, ${operands[0]})`
      }else if(operator==='ALERTME') {
        return `WF.fcnNotifyMe(CTX, 2, ${operands[0]})`
      }else if(operator==='WATCHER') {
        return 'WF.fcnWatcher(CTX)'
      }else if(operator==='ACTORADD') {
        return `WF.fcnActorAdd(CTX, ${operands[0]})`
      }else if(operator==='ACTORDEL') {
        return `WF.fcnActorDel(CTX, ${operands[0]})`
      }else if(operator==='LISTADD') {
        return `WF.fcnListAdd(CTX, [${operands.join(',')}])`
      }else if(operator==='LISTDEL') {
        return `WF.fcnListDel(CTX, [${operands.join(',')}])`
      }else if(operator==='INLIST' || operator==='ALLINLIST') {
        return `WF.fcnAllInList(CTX, [${operands.join(',')}])`
      }else if(operator==='ANYINLIST') {
        return `WF.fcnAnyInList(CTX, [${operands.join(',')}])`
      }else if(operator==='FUNCTION') {
        return `await WF.fcnFunction.call(this, CTX, [${operands.join(',')}])`
      }else if(operator==='SCRIPT') {
        return `await WF.fcnScript.call(this, CTX, [${operands.join(',')}])`
      }else if(operator==='COERCE') {
        return `await WF.fcnCoerce.call(this, CTX, [${operands.join(',')}])`
      }else if(operator==='TIME') {
        if(CTX.cron===true) return true
        else return false
      }else if(operator==='TIMEOUT') {
        if(CTX.cron===true) return true
        else return false
      }else if(operator==='DEBUG') {
        return `WF.fcnDebug.call(this, CTX, [${operands.join(',')}])`
      }else if(operator==='ARRAY') {
        return `[${operands.join(',')}]`
      }else if(operator==='CONCAT') {
        return '('+operands.map(x=>_.toString(x)).join('+')+')'
      }else if(operator==='APPTYPE') {
        return `WF.fcnIsAppType.call(this, CTX, ${operands[0]})`
      }else if(operator==='ACCESS') {
        return `this.hasAccess(CTX, ${operands[0]}, ${operands[1]})`
      }else if(operator==='APPACCESS') {
        return `this.appAccess(CTX, ${operands[0]}, ${operands[1]})`
      }else if(operator==='SQL') {
        return `this.fcnSql(CTX, [${operands.join(',')}])`
      }else if(operator==='FILL') {
        // used to fill mele-input in formready hook
        return `WF.fcnFill(CTX, ${operands[0]}, ${operands[1]})`
      }else if(operator==='PUBLISH') {
        return `this.publish(${operands[0]}, ${operands[1]}, ${operands[2]})`
      }else if(operator==='NULL') {
        return 'null'
      }else{
        this.context_object.error(`Unknown workflow function operator "${operator}"`)
        return false
      }
    }else if(typeof expr==='object' && expr!=null) {
      return '{'+Object.keys(expr).map(p=>{
        return '"'+p+'":'+this._expressionToFunctionPart(CTX, expr[p])
      }).join(',')+'}'
    }else if(typeof expr==='string') {
      return JSON.stringify(expr)
    }else{
    // it is literal, keep as it is
      return expr
    }
  } // _expressionToFunctionPart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * creates SQL query to obtain ID of processes matching provided conditions tree
 * @param {object} CTX - hash of known values
 * @param {array} conditions - list of conditions
 */
  expressionToSql(table, CTX, expr) {
    if(expr===false) return expr
    if(!Array.isArray(expr) || expr.length===0) expr = true
    expr = this.fillExpression(CTX, expr)
    if(expr===false) return expr

    // expr = this.simplifyExpression(CTX, expr);
    if(expr===false) return expr
    if(expr===true) return `SELECT DATA.* FROM ${table} DATA`
    const where = this._expressionToSqlPart(CTX, expr)
    const sql = `SELECT DATA.* FROM ${table} DATA WHERE ${where}`
    return sql
  } // expressionToSql

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * converts generic expression tree into SQL condition
 * @param {array} expr - expression in form [operator, operands...]
 */
  _expressionToSqlPart(CTX, expr) {
    if(Array.isArray(expr)) {
    // it is expression, i.e. array with operator and operands
      if(expr.length===0) return 'true' // empty array is treated as TRUE (i.e. empty condition is satisfied)
      const operator = expr[0]
      let operands = expr.slice(1)
      operands = operands.map(this._expressionToSqlPart.bind(this, CTX))

      if(operator==='NULL') {
        return 'NULL'
      }else if(operator==='NOW') {
        return 'NOW()'
      }else if(operator==='UNOW') {
        // The microsecond twin of NOW, and the only one of the two that means the same thing in
        // both compilers. An ACL is tested twice - in SQL when a watcher's first answer is
        // searched, in JS when publishAction notifies it of a change - so a condition about time
        // has to compile to the same unit on both paths. NOW cannot: here it is a timestamp,
        // in the JS compiler it is unow(). Anything comparing a `*_dt` field against "now minus
        // something" wants this one.
        return '(EXTRACT(EPOCH FROM NOW())*1000000)'
      }else if(operator==='INTERVAL') {
        const op1 = String(operands[0])
        let op2 = String(operands[1])
        op2 = op2.replace(/^'/, '').replace(/'$/, '') // de-escape the column name
        return `INTERVAL '1 ${op2}'*${op1}`
      }else if(operator==='IS') {
        const op = operands[0]
        if(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0' || op==='NULL') return 'false'
        if(/^true$/i.test(operands[0])) return 'true'
        return `NOT(NOT(${operands[0]}))`
      }else if(operator==='NOT') {
        const op = operands[0]
        if(/^false$/i.test(op) || op==null || typeof (op)==='undefined' || op=='' || op==='0' || op==='NULL') return 'true'
        if(/^true$/i.test(operands[0])) return 'false'
        return `NOT(${operands[0]})`
      }else if(operator==='MATCH') {
        return `(${operands[0]} ~ ${operands[1]})`
      }else if(operator==='IMATCH') {
        return `(${operands[0]} ~* ${operands[1]})`
      }else if(operator==='IN') {
        const first = operands.shift()
        return `(${first} IN (${operands.join(',')}))`
      }else if(operator=='==') {
        const op1 = operands[0]
        const op2 = operands[1]
        if(op1==op2) return 'true'
        if(op1==='NULL') return `(${op2} IS NULL)`
        if(op2==='NULL') return `(${op1} IS NULL)`
        return `(${op1} = ${op2})`
      }else if(operator==='!=') {
        const op1 = operands[0]
        const op2 = operands[1]
        if(op1==op2) return 'false'
        if(op1==='NULL') return `(${op2} IS NOT NULL)`
        if(op2==='NULL') return `(${op1} IS NOT NULL)`
        return `(${op1} != ${op2})`
      }else if(operator==='INTEGER') {
        const op1 = operands[0]
        return `COALESCE(NULLIF(regexp_replace(${op1}::text, '[^0-9-]', '', 'g'), '')::integer,0)`
      }else if(operator==='DECIMAL') {
        const op1 = operands[0]
        return `COALESCE(NULLIF(regexp_replace(${op1}::text, '[^0-9.-]', '', 'g'), '')::numeric,0)`
      }else if(operator==='STRING') {
        const op1 = operands[0]
        return `COALESCE(${op1}::text, '')`
      }else if(operator==='++') {
        const op1 = operands[0]
        return `(COALESCE(NULLIF(regexp_replace(${op1}, '[^0-9]', '', 'g'), '')::int, 0) + 1)`
      }else if(operator==='--') {
        const op1 = operands[0]
        return `(COALESCE(NULLIF(regexp_replace(${op1}, '[^0-9]', '', 'g'), '')::int, 0) - 1)`
      }else if(['+', '-', '*', '/'].indexOf(operator)>=0) {
        return `(${operands[0]} ${operator} ${operands[1]})`
      }else if(['<', '<=', '>', '>='].indexOf(operator)>=0) {
        return `(${operands[0]} ${operator} ${operands[1]})`
      }else if(operator==='AND') {
        const ops = []
        for(let i=0; i<operands.length; i++) {
          const op = operands[i]
          if(/^false$/i.test(op)) return 'false'
          if(/^true$/i.test(op)) continue
          ops.push(op)
        }
        if(ops.length===0) return true
        return '('+ops.join(' AND ')+')'
      }else if(operator==='OR') {
        const ops = []
        for(let i=0; i<operands.length; i++) {
          const op = operands[i]
          if(/^true$/i.test(op)) return 'true'
          if(/^false$/i.test(op)) continue
          ops.push(op)
        }
        if(ops.length===0) return false
        return '('+ops.join(' OR ')+')'
      }else if(operator==='GET') {
        if(operands[0]==="'data'") {
          let op2 = String(operands[1])
          op2 = op2.replace(/^'/, '').replace(/'$/, '') // de-escape the column name
          return `DATA.${op2}`
        }else{
          return 'NULL'
        }
      }else if(operator==='CONCAT') {
        return '('+operands.join(' || ')+')'
      }else if(operator==='APPTYPE') {
        const apptype = _.toString(_.get(CTX, 'actor.apptype')).replace(/\W+/g, '')
        let types = _.toString(operands[0])
        if(/^'(.*)'$/.test(types)) {
        // types are specified by constant, we can evaluate it directly without SQL
          types = types.replace(/^'(.*)'$/, '$1').split(',')
          const result = types.indexOf(apptype)>=0 ? 'true' : 'false'
          return result
        }else{
          return `(SELECT POSITION(','||'${apptype}'||',' IN ','||${operands[0]}||',')>0)`
        }
      }else if(operator==='WATCHER') {
        return this.fcnWatcher(CTX)
      }else if(operator==='INLIST' || operator==='ALLINLIST') {
        operands = operands.map(op=>_.toString(op).replace(/\W/g, ''))
        const field = operands.shift()
        if(operands.length===0) return 'true'
        const conditions = operands.map(op=>`DATA.${field} ~ '(^|∫)${op}(∫|$)'`)
        return '('+conditions.join(' AND ')+')'
      }else if(operator==='ANYINLIST') {
        operands = operands.map(op=>_.toString(op).replace(/\W/g, ''))
        const field = operands.shift()
        if(operands.length===0) return 'true'
        operands = operands.map(op=>_.toString(op).replace(/\W/g, ''))
        return `(DATA.${field} ~ '(^|∫)(${operands.join('|')})(∫|$)')`
      }else if(operator==='ACCESS') {
        const user_zones = this.getUserRoleZones(_.get(CTX, 'actor.user_id'), operands[0])
        if(user_zones.length===0) return 'false' // user is in no zone in the required role
        const object_roles = _.toString(operands[1]).match(/\w+/g)

        // if user has access to ALL group and no specific role for object is needed, access is granted
        if(user_zones[0]===1 && !object_roles) return true

        const conditions = []
        if(object_roles) {
          user_zones.forEach(zone=>{
            object_roles.forEach(role=>{
              conditions.push(`,${role}:${zone},`)
            })
          })
        }else{
          user_zones.forEach(zone=>{
            conditions.push(`:${zone},`)
          })
        }
        return `(DATA.roles ~ '${conditions.join('|')}')`
      }else if(operator==='APPACCESS') {
        const app_id = CTX.actor?.app_id
        const app = this.context_object?.apps?.[app_id]
        if(!app) return 'false' // application not found, no access

        // extract list of application roles for the check
        let desired_app_roles
        if(Array.isArray(operands[0])) {
          desired_app_roles = operands[0]
        }else {
          desired_app_roles = String(operands[0]??'')
          if(desired_app_roles==='null' || desired_app_roles==='NULL' || desired_app_roles==='\'*\'') {
            desired_app_roles = null
          }else {
            desired_app_roles = desired_app_roles.match(/\w+/g)
          }
        }
        if(desired_app_roles && desired_app_roles.length===0) return 'false' // no application roles specified, no access

        const app_role_zones = String(app?.roles ?? '')
        if(app_role_zones==='') return 'false' // application has no roles at all

        // extract list of object roles for the check
        let desired_obj_roles
        if(Array.isArray(operands[1])) {
          desired_obj_roles = operands[1]
        }else {
          desired_obj_roles = String(operands[1]??'')
          if(desired_obj_roles==='null' || desired_obj_roles==='NULL' || desired_obj_roles==='\'*\'') {
            desired_obj_roles = null
          }else {
            desired_obj_roles = desired_obj_roles.match(/\w+/g)
          }
        }
        if(desired_obj_roles && desired_obj_roles.length===0) return 'false' // no object roles specified, no access

        // find zones for which application has desired roles
        const app_zones = app_role_zones
          .split(',')
          .map(r=>r.split(':'))
          .filter(r=>r.length===2 && r[0]!=='' && r[1]!=='' && (desired_app_roles===null || desired_app_roles.includes(r[0])))
          .map(r=>r[1])
        if(app_zones.length===0) return 'false' // application has no zones where it is in desired roles

        const conditions = []
        if(desired_obj_roles) {
          for(const role of desired_obj_roles) {
            for(const zone of app_zones) {
              conditions.push(`,${role}:${zone},`)
            }
          }
        }else{
          for(const zone of app_zones) {
            conditions.push(`:${zone},`)
          }
        }
        return `(DATA.roles ~ '${conditions.join('|')}')`
      }else if(operator==='TIME') {
        return 'false'
      }else if(operator==='TIMEOUT') {
        return 'false'
      }else{
        this.context_object.error(`Unknown workflow sql operator "${operator}": ${expr.join(',')}`)
        return 'false'
      }
    }else if(typeof expr==='string') {
      return "'"+expr.replace(/'/g, "''")+"'"
    }else{
    // it is literal, keep as it is
      return expr
    }
  } // _expressionToSqlPart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * returns list of actions allowed for given context (should contain at least "data" subhash to work reasonably...)
 * @param {object} values - values hash
 * @param {object} values.data - data of the process
 * @param {object} values.actor - data of the actor
 */
  async allowedActions(listener, values) {
    const state = _.toString(_.get(values, 'data.state'))
    const actions = _.get(this.states, [state, 'actions'], {})
    const allowed_actions = []
    for(const a in actions) {
      const test = this.expressionToFunction(values, actions[a].condition).bind(listener)
      if(await test.call(this.context_object, values)) allowed_actions.push(a)
    }
    return allowed_actions
  } // allowedActions

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Returns definition of action for given record
 * Returns null if no action exists matching record's state+action.
 * @param {object} action - action name
 */
  recordActionDefinition(record, action) {
    const state = _.get(record, 'state', '')
    action = _.toString(action)
    if(action==='') return null
    const action_def = _.get(this, ['states', state, 'actions', action])
    return _.isObjectLike(action_def) ? action_def : null
  } // recordActionDefinition

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Returns name of form to be displayed for given state+action.
 * Returns null if no action exists matching state+action.
 * Returns empty string "" if trnasition exists but no form is defined for it.
 * @param {string} record - state name
 * @param {string} action - action name
 */
  formForAction(record, action) {
    const state = _.get(record, 'state', '')
    action = _.toString(action)
    if(action==='') return null
    const action_def = _.get(this, ['states', state, 'actions', action])
    if(!_.isObjectLike(action_def)) return null
    let form = this.normalizeExpression(_.toString(action_def.form))
    form = this.fillExpression({ data: record }, form)
    return form
  } // formForAction

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Transforms file/image fields of new_obj into MD5 if old value contains its data already.
 * Used to limit data sent through network for file attachments.
 * @param {object} old_obj - old object attributes
 * @param {object} new_obj - new object attributes
 */
  encodeFileFields(old_obj, new_obj) {
    if(!_.isObjectLike(old_obj) || !_.isObjectLike(new_obj)) return
    const fields = this.fields || {}
    const file_fields = Object.keys(fields).filter(field_name=>/^(file|image)s?$/.test(fields[field_name].type))
    file_fields.forEach(field_name=>{
      const old = old_obj[field_name]
      const val = new_obj[field_name]
      if(old && val) { new_obj[field_name] = this.encodeFileField(old, val) }
    })
  } // encodeFileFields

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Transforms large chunks separated by ∫ into their MD5 if they did not change
 * Used to limit data sent through network for file attachments.
 * @param {string} old - old value
 * @param {string} change - new value
 */
  encodeFileField(old, change) {
    if(typeof old !== 'string' || old==='') return change
    if(typeof change !== 'string' || change==='') return change
    old = old.split('∫')
    change = change.split('∫').map(item=>{
      if(old.indexOf(item)<0) return item // item is new, return as it is
      return 'MD5:'+mcrypt.md5(item)
    })
    return change.join('∫')
  } // encodeFileField

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Transforms file/image fields of new_obj from MD5 if old value contains its data already.
 * Used to limit data sent through network for file attachments.
 * @param {object} old_obj - old object attributes
 * @param {object} new_obj - new object attributes
 */
  decodeFileFields(old_obj, new_obj) {
    if(!_.isObjectLike(old_obj) || !_.isObjectLike(new_obj)) return
    const fields = this.fields || {}
    const file_fields = Object.keys(fields).filter(field_name=>/^(file|image)s?$/.test(fields[field_name].type))

    let missing_files = false
    file_fields.forEach(field_name=>{
      const old = old_obj[field_name]
      const val = new_obj[field_name]
      if(val) {
        new_obj[field_name] = this.decodeFileField(old, val)
        if(/MD5:/.test(new_obj[field_name])) {
          missing_files = true
        }
      }
    })
    return missing_files
  } // decodeFileField

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
 * @description
 * Transforms large chunks separated by ∫ from their MD5 using original
 * Used to limit data sent through network for file attachments.
 * @param {string} old - old value
 * @param {string} change - new value
 */
  decodeFileField(old, change) {
    if(typeof old !== 'string' || old==='') return change
    if(typeof change !== 'string' || change==='') return change
    old = old.split('∫')
    const old_md5 = []
    change = change.split('∫').map(item=>{
      if(!/^MD5:(.*)/.test(item)) return item
      item = item.substr(4)
      if(item==='') return item
      for(let i=0; i<old.length; i++) {
      // this a bit strange testing is to minimize counting of MD5 for old items
        const md5 = old_md5[i] = old_md5[i] || mcrypt.md5(old[i])
        if(item===md5) return old[i]
      }
      return item // we cannot convert, so just keep it as it is
    })
    return change.join('∫')
  } // decodeFileField

  /**
 * @description
 * Set default setting for fields in forms
 * @param {*} form_name
 * @param {*} form
 * @param {*} workflow_id - ID of workflow
 * @param {*} forms
 * @param {*} fields - fields defined in workflow
 */
  static fillFormDefaults(form_name, form, workflow_id, forms, fields) {
    if(Array.isArray(form)) return form.forEach(f=>msysWorkflow.fillFormDefaults(form_name, f, workflow_id, forms, fields))
    if(!form || typeof (form)!=='object') return
    if(form.include) {
      const ref = forms[form.include]
      if(ref) _.defaultsDeep(form, ref)
      delete form.include
    }

    if(Array.isArray(form.items)) form.items = form.items.map(item => _.cloneDeep(ITEM_TEMPLATES[item]) || (typeof (item)==='string' ? (/^#+/.test(item) ? { field: item.substr(1), hidden: true } : { field: item }) : item))

    if(Array.isArray(form.columns)) {
      form.columns = form.columns.map(col=>{
        if(/^#\w+/.test(col)) {
          // special case: create hidden column
          // useful to retrieve this field from workflow but not show it to user
          // e.g. for WORKFLOW.computeCalculatedFields
          col = { key: col.substr(1), hidden: true }
        }else if(typeof (col)==='string') {
          const def = fields[col] || FIELD_DEFAULT_INPUT[col] || {}
          col = { key: col }
          if(def.type==='born') col.timeformat = 'format.date'
          else if(/date|time/.test(def.type)) col.timeformat = def.format || 'format.'+def.type
          else if(def.input === 'duration') col.after = 's'
          else if(def.input === 'durationms') col.after = 'ms'
          if(def.prefix) col.prefix = def.prefix
          if(def.before) col.before = def.before
          if(def.after) col.after = def.after
          col.label = def.label || ('wf.'+workflow_id+'.'+col.key)
        }
        return col
      })
    }
    if(form.field) {
      const pure_field = _.toString(form.field).replace(/^(\^|!|~|<=?|>=?)/, '') // get actual name of field without special prefixes marking custom search operation
      let def = FIELD_DEFAULT_INPUT[pure_field]
      if(!def && fields[pure_field]) {
        def = _.pick(fields[pure_field], FIELD_COPIED_ATTRIBUTES)
        if(!def.input) {
          if(def.type==='list') {
            def.input = 'slist'
          }else if(def.type==='image') {
            def.input = 'image'
          }else if(def.type==='images') {
            def.input = 'images'
            def.multi = true
          }else if(def.type==='signature') {
            def.input = 'signature'
            def.multi = true
          }else if(def.type==='file') {
            def.input = 'files'
          }else if(def.type==='files') {
            def.input = 'files'
            def.multi = true
          }else if(def.type==='geoshape') {
            def.input = 'geoshape'
          }else if(/^date|time|datetime|datetimeus$/.test(def.type)) {
            def.input = def.type
            if(typeof (def.blank)==='undefined') def.blank = true
          }else if(def.prefix==='country.' || def.prefix==='country_with_code.') {
            def.input = 'slookup'
            if(typeof (def.blank)==='undefined') def.blank = true
          }else if(def.prefix) {
            def.input = 'sdropdown'
            if(typeof (def.blank)==='undefined') def.blank = true
          }
        }
        def.type = def.input || 'string'
        delete def.input
        def.tag = 'mele-input'
        if(typeof (def.label)==='undefined') def.label = 'wf.'+workflow_id+'.'+pure_field
        if(/filter/.test(form_name)) {
        // by default all filter dropdowns could be blank
          def.blank = true
          def.readonly = false
          if(def.format==='notblank') def.format=''
        }
      }
      if(def) def.key = form.field
      _.defaults(form, def)
      delete form.field
      if(form.prefix && typeof (form.auto)==='undefined') form.auto = true
      if(form.prefix && typeof (form.itemlabel)==='undefined') form.itemlabel = '${prefix}${key}'
    }
    ATTRIBUTES_TO_REPLACE_WF_WILDCARD.forEach(attr=>{
      if(typeof (form[attr])==='string') form[attr] = form[attr].replace(/\$wf\b/g, 'wf.'+workflow_id)
    })
    if(Array.isArray(form.items)) form.items.forEach(f=>msysWorkflow.fillFormDefaults(form_name, f, workflow_id, forms, fields))
    if(Array.isArray(form.columns)) form.columns.forEach(f=>msysWorkflow.fillFormDefaults(form_name, f, workflow_id, forms, fields))
    return form
  } // fillFormDefaults

  getSVG(data, params) {
    const unique_id = Date.now()

    if(!_.isObjectLike(params)) params = {}
    params.lang = params.lang || 'en'

    // find maximum "y"
    const states = this.states
    let max_y = -3
    for(const state_name in states) {
      try{
        const y = states[state_name].draw.y
        if(max_y<y) max_y = y
      }catch(e) {}
    }

    let svg = `<svg class="workflow" xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 30 ${max_y+2}" xmlns:xlink="http://www.w3.org/1999/xlink" style="border: 1px solid hsl(20,20%,50%); background: hsl(20,20%,90%)">
  <style>
    svg.workflow * {
      font-family: Lucida Console;
      font-family: Courier New;
      font-family: monospace;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    svg.workflow .state rect {
      fill: white;
      stroke: black;
      stroke-width: 0.05px;
    }
    svg.workflow .state text {
      font-size: 0.7px;
      stroke-width: 0.1px;
      text-anchor: middle;
      dominant-baseline: central;
    }
    svg.workflow .state.current rect {
      fill: hsl(220, 67%, 60%)
    }
    svg.workflow .state.current text {
      fill: #fff;
    }
    svg.workflow .state.start rect {
      rx: 50%;
      stroke: hsl(100,67%,40%);
      stroke-width: 0.15px;
    }
    svg.workflow .state.start text {
      fill: hsl(100,67%,40%);
    }
    svg.workflow .state.wiped rect {
      rx: 1;
      fill: #fff8;
      stroke: hsl(355,67%,80%);
    }
    svg.workflow .state.wiped text {
      fill: hsl(355,67%,80%);
    }

    svg.workflow .action marker {
      marker-width: 20px;
    }
    svg.workflow .action marker path {
      stroke-width: 1px;
    }
    svg.workflow .action path {
      stroke-width: 0.06px;
      stroke: #111;
      fill: none;
    }
    svg.workflow .action.out text {
      text-anchor: start;
    }
    svg.workflow .action text {
      dominant-baseline: ideographic;
      text-anchor: middle;
      font-size: 0.3px;
      stroke-width: 0.03px;
      stroke: #111;
      fill: #111;
    }
    svg.workflow .action textPath {
      alignment-baseline: ideographic;
      font-family: opensans_light;
    }
    svg.workflow .action.myself * {
      stroke: hsl(20deg 20% 56%) !important;
    }
  </style>
  `

    // display state node
    for(const state_name in states) {
      const state = states[state_name]
      if(!state) continue
      if(!_.isObjectLike(state.draw)) state.draw = {}
      const rect = state.draw
      if(!(rect.x>=0)) rect.x = 10
      if(!(rect.y>=0)) {
        max_y += 5
        rect.y = max_y
      }
      const state_label = state_name || 'START'
      const state_label_tr = (params.lang ? LANG.translate(params.lang, 'wf.'+this.id+'.state.'+state_label, true) || state_label : state_label).toUpperCase()

      rect.w = rect.w || state_label_tr.length*0.5 + 0.5
      rect.h = rect.h || 1
      rect.x1 = rect.x - rect.w/2
      rect.x2 = rect.x + rect.w/2
      rect.y1 = rect.y - rect.h/2
      rect.y2 = rect.y + rect.h/2

      let cls = 'state'
      if(state_name==data.state) cls += ' current'
      if(state_name==='') cls += ' start'
      if(state_name==='WIPED') cls += ' wiped'
      svg += `
    <g class="${cls}">
      <rect x="${rect.x1}" y="${rect.y1}" width="${rect.w}" height="${rect.h}" rx="0.2" />
      <text x="${rect.x}" y="${rect.y}">${state_label_tr}</text>
    </g>
    `
    }

    // display action lines
    const combinations = {}
    let line_idx = 0
    for(const state_name in states) {
      const state = states[state_name]
      if(!state) continue
      const rect1 = state.draw

      const actions = state.actions || {}
      for(const action_name in actions) {
        const action = actions[action_name]
        if(!_.isObjectLike(action)) continue
        const target_name = action.target_state || state_name
        const target = states[target_name]
        const rect2 = target.draw
        // var combination = state_name < target_name ? `${state_name}-${target_name}` : `${target_name}-${state_name}`
        const combination = `${state_name}-${target_name}`
        const nth_line = combinations[combination] = (combinations[combination] || 0) + 1
        let path
        let cls = 'action'
        let offset
        if(target_name == state_name) {
        // draw circle on the same state
          const nth_coef = 1.5
          path = `M${rect1.x2-0.5},${rect1.y1} C${rect1.x2},${rect1.y - nth_line*nth_coef - 2} ${rect1.x2-0.5 + nth_line*nth_coef + 2},${rect1.y1} ${rect1.x2},${rect1.y}`
          cls += ' myself'
          offset = '50%'
        }else{
        // draw connection between two states
          const margin = 0.15
          const rect1m = Object.assign({}, rect1)
          const rect2m = Object.assign({}, rect2)
          rect1m.x -= margin
          rect1m.y -= margin
          rect1m.w += margin*2
          rect1m.h += margin*2
          rect2m.x -= margin
          rect2m.y -= margin
          rect2m.w += margin*2
          rect2m.h += margin*2
          const intersections = mmath.rectConnections(rect1, rect2)
          const x1 = intersections.x1; const x2 = intersections.x2; const y1 = intersections.y1; const y2 = intersections.y2

          const dx = Math.sqrt((x1-x2)**2 + (y1-y2)**2)
          const diff = dx/3
          const c1x = (dx - diff)/2
          const c1y = nth_line * (-diff)
          const c2x = dx - c1x
          const c2y = c1y
          const phi = Math.atan((y2-y1) / (x2-x1))
          const c1x_moved = c1x * Math.cos(phi) - c1y*Math.sin(phi) + x1
          const c1y_moved = c1x * Math.sin(phi) + c1y*Math.cos(phi) + y1
          const c2x_moved = c2x * Math.cos(phi) - c2y*Math.sin(phi) + x1
          const c2y_moved = c2x * Math.sin(phi) + c2y*Math.cos(phi) + y1
          path = `M${x1},${y1} C${c1x_moved},${c1y_moved} ${c2x_moved},${c2y_moved} ${x2},${y2}`
          cls += ' out'
          offset = '20%'
        }
        line_idx++
        const idx = unique_id + '_'+line_idx
        const action_name_tr = (params.lang ? LANG.translate(params.lang, 'wf.'+this.id+'.action.'+action_name, true) || action_name : action_name).toUpperCase()
        svg += `
      <g class="${cls}">
        <defs><marker id="marker${idx}" orient="auto" markerWidth="20" markerHeight="20" refX="5" refY="3"><path d="M0,1 l5,2 l-5,2" /></marker></defs>
        <path id="line${idx}" marker-end="url(#marker${idx})" d="${path}" />
        <text><textPath startOffset="${offset}" xlink:href="#line${idx}">${action_name_tr}</textPath></text>
      </g>
      `
      }
    }

    svg += '</svg>'
    return svg
  } // getSVG
} // msysWorkflow
